<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useTemplateRefsList } from '@vueuse/core';

import { defaultIncomingProperty, defaultSupportingProperty } from '@/helpers/builder';
import { PropertyDetailOption, ErhlLoanPurpose } from '@/helpers/const';
import api from '@/api';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';
import { useErhlCustomerLoanAppProcess } from '@/helpers/useErhlCustomerLoanAppProcess';
import { PlusCircleIcon } from '@heroicons/vue/20/solid';

import PropertyDetailCard from './modules/PropertyDetails/PropertyDetailCard.vue';

import type { PropertyDetailEntry, IncomingProperty, SupportingProperty } from '@/types';

const { loanApp, state, saveAndGoNext, goBack } = useErhlCustomerLoanAppProcess();
const { flagSessionExpired } = useSessionExpiry();
const route = useRoute();

const isPurchaseNewProperty = computed(() => {
  const purposes = loanApp.value.erhlLoanPurposes ?? [];
  return Array.isArray(purposes) && purposes.includes(ErhlLoanPurpose.PURCHASE_NEW_PROPERTY);
});

// ─── Purchase properties (incoming) ────────────────────────────────
const loadExistingPurchaseProperties = (): { item: PropertyDetailEntry }[] =>
  loanApp.value.incomingProperties.map((p) => ({
    item: { ...p, propertyOptions: PropertyDetailOption.PROPERTY_TO_PURCHASE },
  }));

const purchaseProperties = ref<{ item: PropertyDetailEntry }[]>(loadExistingPurchaseProperties());
const purchaseRefs = useTemplateRefsList<InstanceType<typeof PropertyDetailCard>>();
const purchaseCounter = ref(purchaseProperties.value.length);

const generatePurchaseId = () => `purchase-property-${purchaseCounter.value++}`;

const removePurchaseProperty = (id: string) => {
  purchaseProperties.value = purchaseProperties.value.filter((p) => p.item.id !== id);
};

const getPurchasePropertyCount = (index: number) => index + 1;

// Loan details "Edit funds required" opens the purchase property directly when there's only one
const editPurchaseOnLoad = route.query.edit === 'purchase' && purchaseProperties.value.length === 1;

// ─── Supporting properties ─────────────────────────────────────────
const loadExistingSupportingProperties = (): { item: PropertyDetailEntry }[] =>
  (loanApp.value.supportingProperties ?? []).map((p) => ({
    item: { ...p, propertyOptions: PropertyDetailOption.SUPPORTING_PROPERTY },
  }));

const supportingProperties = ref<{ item: PropertyDetailEntry }[]>(loadExistingSupportingProperties());
const supportingRefs = useTemplateRefsList<InstanceType<typeof PropertyDetailCard>>();
const supportingCounter = ref(supportingProperties.value.length);

const generateSupportingId = () => `supporting-property-${supportingCounter.value++}`;

/** Adds a new supporting property card after validating existing ones. */
const addSupportingProperty = async () => {
  if (await validateSupportingItems()) {
    supportingProperties.value.push({ item: { id: generateSupportingId() } });
  }
};

const removeSupportingProperty = (id: string) => {
  supportingProperties.value = supportingProperties.value.filter((p) => p.item.id !== id);
};

const getSupportingPropertyCount = (index: number) => index + 1;

// When buying, at least one existing property is required, so the last one can't be removed
const canRemoveSupportingProperty = computed(() => supportingProperties.value.length > 1);

// ─── Validation ────────────────────────────────────────────────────
const isLoading = ref(false);
const validationError = ref('');

const validatePurchaseItems = async () => {
  const results = await Promise.all(purchaseRefs.value.map((ref) => ref.update()));
  return results.every(Boolean);
};

const validateSupportingItems = async () => {
  const results = await Promise.all(supportingRefs.value.map((ref) => ref.update()));
  return results.every(Boolean);
};

/**
 * Validates all property cards and checks ERHL-specific requirements:
 * - When "Purchase new property" is selected: at least one existing and one purchase property are required.
 * - Otherwise: at least one supporting property is required.
 */
const validateAll = async (): Promise<boolean> => {
  validationError.value = '';

  if (isPurchaseNewProperty.value) {
    if (supportingProperties.value.length === 0) {
      validationError.value = 'You must add at least one existing property.';
      return false;
    }
    if (purchaseProperties.value.length === 0) {
      validationError.value = 'You must add at least one purchase property.';
      return false;
    }
    const [supportingValid, purchaseValid] = await Promise.all([validateSupportingItems(), validatePurchaseItems()]);
    return supportingValid && purchaseValid;
  }

  if (supportingProperties.value.length === 0) {
    validationError.value = 'You must add at least one supporting property.';
    return false;
  }
  return validateSupportingItems();
};

// ─── Save ──────────────────────────────────────────────────────────
const saveAndContinue = async () => {
  if (!(await validateAll())) return;

  isLoading.value = true;

  const incomingProperties: IncomingProperty[] = isPurchaseNewProperty.value
    ? purchaseProperties.value.map((p) => ({ ...defaultIncomingProperty(), ...p.item }))
    : [];

  const supportingProps: SupportingProperty[] = supportingProperties.value.map((p) => ({
    ...defaultSupportingProperty(),
    ...p.item,
  }));

  try {
    const applicationDraftId = loanApp.value.applicationDraftId;
    saveAndGoNext({
      incomingProperties,
      outgoingProperties: [],
      supportingProperties: supportingProps,
      applicationDraftId,
    });
  } catch (error) {
    if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
      flagSessionExpired();
      return;
    }
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
};

// A supporting property is always required: it's the sole security, or the existing property
// asked for before the property being bought.
watch(
  supportingProperties,
  (val) => {
    if (val.length === 0) {
      supportingProperties.value.push({ item: { id: generateSupportingId() } });
    }
  },
  { immediate: true },
);

// Seed one purchase property if none exist when purchase purpose is active
watch(
  [isPurchaseNewProperty, purchaseProperties],
  ([isPurchase, properties]) => {
    if (isPurchase && properties.length === 0) {
      purchaseProperties.value.push({ item: { id: generatePurchaseId() } });
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="w-full">
    <div class="mb-8 pb-2">
      <h2 v-if="isPurchaseNewProperty" class="text-2xl font-medium">
        Tell us about your
        <BxHighlight>existing property</BxHighlight>
        and the property you're buying
      </h2>
      <h2 v-else class="text-2xl font-medium">
        Tell us about your
        <BxHighlight>property</BxHighlight>
      </h2>
      <p v-if="isPurchaseNewProperty" class="mt-1 text-sm text-gray-500">
        Your existing property (or properties) will be used as security for the loan.
      </p>
      <p v-else class="mt-1 text-sm text-gray-500">
        This property (or properties) will be used as security for the loan.
      </p>
    </div>

    <!-- Property section for purchase mode: supporting properties are always shown before the purchase -->
    <div v-if="isPurchaseNewProperty" class="section-panel md:p-6">
      <div class="mb-6">
        <h3 class="hidden text-lg font-medium leading-6 text-gray-900 sm:block">Property</h3>
      </div>
      <template v-for="(property, index) in supportingProperties" :key="property?.item?.id">
        <PropertyDetailCard
          :ref="supportingRefs.set"
          v-model="property.item"
          property-mode="supporting"
          :index="getSupportingPropertyCount(index)"
          class="mb-4"
          is-multiple
          :removable="canRemoveSupportingProperty"
          @remove="removeSupportingProperty"
          @saved="() => {}"
        />
      </template>
      <template v-for="(property, index) in purchaseProperties" :key="property?.item?.id">
        <PropertyDetailCard
          :ref="purchaseRefs.set"
          v-model="property.item"
          property-mode="purchase"
          :index="getPurchasePropertyCount(index)"
          class="mb-4"
          is-multiple
          :start-editing="editPurchaseOnLoad"
          @remove="removePurchaseProperty"
          @saved="() => {}"
        />
      </template>
      <div class="mt-6 flex justify-end">
        <BxButton id="purchase-property-add-supporting" variant="tertiary" @click="addSupportingProperty()">
          <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          Add another property
        </BxButton>
      </div>
    </div>

    <!-- Supporting property section (when "Purchase new property" is NOT selected) -->
    <div v-else class="section-panel md:p-6">
      <div class="mb-6">
        <h3 class="hidden text-lg font-medium leading-6 text-gray-900 sm:block">Property</h3>
      </div>
      <template v-for="(property, index) in supportingProperties" :key="property?.item?.id">
        <PropertyDetailCard
          :ref="supportingRefs.set"
          v-model="property.item"
          property-mode="supporting"
          :index="getSupportingPropertyCount(index)"
          class="mb-4"
          is-multiple
          @remove="removeSupportingProperty"
          @saved="() => {}"
        />
      </template>
      <div class="mt-6 flex justify-end">
        <BxButton id="supporting-property-add" variant="tertiary" @click="addSupportingProperty()">
          <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          Add another property
        </BxButton>
      </div>
    </div>

    <BxErrorPanel v-if="validationError" class="mt-6">
      {{ validationError }}
    </BxErrorPanel>

    <div class="mt-6">
      <BxButton id="property-details-continue" variant="primary" :loading="isLoading" @click="saveAndContinue()">
        Save and continue
      </BxButton>
      <BxButton v-if="state.nextEvents.includes('BACK')" variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>
